/**
 * Daily 에 붙는 것들의 시드 — 코멘트(`daily_comment`), 사진(`daily_image`), 수정 요청
 * (`daily_edit_request`).
 *
 * 코멘트는 Daily 값과 집계를 바꾸지 않는다. 사진은 파일 바이트를 둘 곳이 없어 mock 은 메타데이터만
 * 들고 있고, 내용 조회는 handler 가 작은 자리표시 이미지를 돌려준다.
 */
import type { DailyComment, DailyEditRequest, DailyImage, EditRequestStatus } from "@/types";
import { dailies } from "./dailies";
import { PM_OF } from "./projects";
import { at, rng } from "./seed";

export interface MockComment extends DailyComment {
  daily_table_id: string;
  deleted_at: string | null;
}

export interface MockImage extends DailyImage {
  daily_table_id: string;
}

const dailyOf = (userId: string, date: string) =>
  dailies.find((row) => row.user_id === userId && row.work_date === date);

const staffDaily = dailyOf("u-staff", "2026-10-21");
const pmDaily = dailyOf("u-pm", "2026-10-20");

const fixedComments: MockComment[] = staffDaily
  ? [
      {
        id: "daily-comment-1",
        daily_table_id: staffDaily.id,
        daily_row_id: null,
        author_id: "u-pm",
        body: "오늘 시간이 좀 많아 보여요. 어떤 작업이었는지 한 줄 더 적어 주세요.",
        created_at: at("2026-10-21", "19:12"),
        updated_at: null,
        deleted_at: null,
      },
      {
        id: "daily-comment-2",
        daily_table_id: staffDaily.id,
        daily_row_id: null,
        author_id: "u-staff",
        body: "창호 상세가 생각보다 오래 걸렸습니다. 다음 줄에 나눠 적겠습니다.",
        created_at: at("2026-10-21", "20:02"),
        updated_at: null,
        deleted_at: null,
      },
    ]
  : [];

const fixedImages: MockImage[] = pmDaily
  ? [
      {
        id: "daily-image-1",
        daily_table_id: pmDaily.id,
        project_id: pmDaily.rows[0]?.project_id ?? null,
        daily_row_id: null,
        file_name: "현장-골조-01.jpg",
        content_type: "image/jpeg",
        byte_size: 482_118,
        position: 1,
        uploaded_by: "u-pm",
        created_at: at("2026-10-20", "18:40"),
      },
    ]
  : [];

/* ── 작업별 코멘트와 사진 — 최근 열흘 남짓 ─────────────────────────────── */

/** PM 이나 관리자가 작업에 남기는 말과, 쓴 사람이 다는 답. */
const REVIEW = [
  "수고했어요. 이 부분 진행 상황 내일 공유해 주세요.",
  "발주처가 이 건 일정 물어봤어요. 대략 언제쯤 될까요?",
  "시간 배분 좋네요. 이대로 가요.",
  "도면 번호도 같이 적어 주세요.",
  "발주처 회신 오면 이 작업에 이어서 남겨 주세요.",
  "이 부분은 내일 회의 전에 한 번 같이 봐요.",
  "시간이 계획보다 많이 들었네요. 막힌 데가 있었나요?",
  "좋습니다. 이대로 발주처에 보내도 될 것 같아요.",
  "구조 쪽 의견도 받아서 반영해 주세요.",
  "사진 몇 장 더 올려 주면 주간 보고서에 쓰기 좋겠어요.",
];
const REPLY = [
  "네, 내일 오전 회의 때 말씀드릴게요.",
  "이번 주 금요일까지는 될 것 같습니다.",
  "네, 반영해서 다시 올릴게요.",
  "확인했습니다. 내일 오전에 마무리할게요.",
  "구조팀 회신 기다리는 중이에요.",
  "사진 추가했습니다.",
];
const PHOTO = ["현장", "외관", "조형물", "준공", "모형"];
/** 하루 전체에 남기는 말 — 줄을 가리지 않는 피드백. */
const DAY_NOTE = [
  "오늘 연장이 좀 많았네요. 내일은 일찍 들어가세요.",
  "이번 주 계획 대비 잘 가고 있어요.",
  "회의 정리 고마워요. 다음 주 일정에 반영할게요.",
  "현장 다녀온 내용 Weekly 에 한 줄 남겨 주세요.",
];

const pmOf = (projectId: string | null) => (projectId ? PM_OF.get(projectId) : undefined);

/**
 * 코멘트가 붙는 범위 — 마지막 Daily 부터 3주 전까지, 오늘 이전 날만(아직 하루가 끝나지 않은 날에는 피드백이
 * 없다). 직원으로 지난 Daily 를 몇 날만 넘겨 봐도 코멘트가 보이게 넉넉히 둔다.
 */
const recentFrom = (() => {
  const last = dailies.reduce((max, d) => (d.work_date > max ? d.work_date : max), "");
  const date = new Date(`${last}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 21);
  return date.toISOString().slice(0, 10);
})();
const todayIso = new Date().toISOString().slice(0, 10);
const daysBefore = (days: number) => {
  const last = dailies.reduce((max, d) => (d.work_date > max ? d.work_date : max), "");
  const date = new Date(`${last}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
};
const rowsOf = (list: typeof dailies, seed: string) =>
  list.flatMap((daily) =>
    daily.rows.map((row) => ({ daily, row, r: rng(`${seed}:${daily.id}:${row.id}`) }))
  );

/** 코멘트를 받는 줄 — 3주 안, 오늘 이전 */
const generated = rowsOf(
  dailies.filter((d) => d.work_date >= recentFrom && d.work_date < todayIso),
  "extras"
);
/** 사진을 붙이는 줄 — 2주 안(오늘 포함 — 하루 중에도 올린다) */
const photoRows = rowsOf(
  dailies.filter((d) => d.work_date >= daysBefore(14)),
  "photo"
);

let commentSeq = 0;
const reviewComments: MockComment[] = generated.flatMap(({ daily, row, r }) => {
  if (!r.chance(0.35)) return [];
  // 프로젝트 작업은 그 PM 이, 아니면 관리자(김태호)가 단다. 자기 작업이면 관리자가 단다.
  const pm = pmOf(row.project_id);
  const reviewer = pm && pm !== daily.user_id ? pm : "u-owner";
  if (reviewer === daily.user_id) return [];
  const comment = (author: string, body: string, time: string): MockComment => ({
    id: `daily-comment-g${++commentSeq}`,
    daily_table_id: daily.id,
    // 코멘트는 Daily 통째로(그 사람의 하루에) 단다 — 줄을 가리지 않는다.
    daily_row_id: null,
    author_id: author,
    body,
    created_at: at(daily.work_date, time),
    updated_at: null,
    deleted_at: null,
  });
  const first = comment(reviewer, r.pick(REVIEW), `1${1 + r.int(7)}:${String(10 + r.int(49))}`);
  return r.chance(0.45)
    ? [first, comment(daily.user_id, r.pick(REPLY), `1${8}:${String(10 + r.int(49))}`)]
    : [first];
});

let imageSeq = 0;
const taskImages: MockImage[] = photoRows.flatMap(({ daily, row, r }) => {
  // 한 일에 붙인 사진 — 끝낸 프로젝트 작업의 절반쯤에, 하루·프로젝트당 상한(3장) 안에서. 사진은
  // 사무소 작업 사진 다섯 장 중에서 보인다(→ dailyPhotos.ts).
  if (!row.project_id || !row.description || !r.chance(0.5)) return [];
  return Array.from({ length: 1 + r.int(3) }, (_, i) => ({
    id: `daily-image-g${++imageSeq}`,
    daily_table_id: daily.id,
    project_id: row.project_id,
    daily_row_id: row.id,
    file_name: `${r.pick(PHOTO)}-${String(i + 1).padStart(2, "0")}.jpg`,
    content_type: "image/jpeg",
    byte_size: 300_000 + r.int(400_000),
    position: i + 1,
    uploaded_by: daily.user_id,
    created_at: at(daily.work_date, "17:30"),
  }));
});

/** 하루 전체 코멘트 — Daily 의 4분의 1 쯤에 관리자가 한 줄, 그중 절반에 쓴 사람의 답. */
const dayComments: MockComment[] = dailies
  .filter((d) => d.work_date >= recentFrom && d.work_date < todayIso && d.user_id !== "u-owner")
  .flatMap((daily) => {
    const r = rng(`day-comment:${daily.id}`);
    if (!daily.rows.some((row) => row.actual_hours > 0) || !r.chance(0.25)) return [];
    const comment = (author: string, body: string, time: string): MockComment => ({
      id: `daily-comment-g${++commentSeq}`,
      daily_table_id: daily.id,
      daily_row_id: null,
      author_id: author,
      body,
      created_at: at(daily.work_date, time),
      updated_at: null,
      deleted_at: null,
    });
    const first = comment("u-owner", r.pick(DAY_NOTE), `19:${String(10 + r.int(49))}`);
    return r.chance(0.5)
      ? [first, comment(daily.user_id, r.pick(REPLY), `20:${String(10 + r.int(49))}`)]
      : [first];
  });

export const dailyComments: MockComment[] = [...fixedComments, ...reviewComments, ...dayComments];

/*
 * ── Daily 수정 요청 ─────────────────────────────────────────────────────────
 * 대기 중인 것 둘은 관리자 Daily 의 「수정 요청」 탭과 알림에서 바로 결재해 보는 몫이다. 나머지는 이미 끝난
 * 것 — 직원 화면의 「내 수정 요청」에 승인 · 반려 · 철회가 모두 보이게 한다(변경민 · 노은진으로 들어가 본다).
 */
const REQUESTS: {
  userId: string;
  name: string;
  reason: string;
  extra: number;
  note: string;
  /** 그 사람의 최근 Daily 중 몇 번째를 고칠까(0 이 가장 최근). 사람 안에서 겹치지 않게 */
  nth: number;
  status: EditRequestStatus;
  decisionNote?: string;
}[] = [
  // 최다민 — 알림 notif-6 과 짝
  {
    userId: "u-st4",
    name: "최다민",
    reason: "현장 대응으로 늦게까지 남았는데 시간을 적게 적었어요.",
    extra: 1.5,
    note: "현장 대응",
    nth: 1,
    status: "pending",
  },
  {
    userId: "u-st3",
    name: "오세진",
    reason: "도면 번호를 빠뜨려서 한 일에 더 적고 싶어요.",
    extra: 0,
    note: "도면 A-201~205",
    nth: 1,
    status: "pending",
  },
  {
    userId: "변경민",
    name: "변경민",
    reason: "구조 검토 회의가 길어졌는데 시간을 적게 적었어요.",
    extra: 1,
    note: "구조 검토 회의",
    nth: 3,
    status: "approved",
  },
  {
    userId: "변경민",
    name: "변경민",
    reason: "현장 미팅을 다녀왔는데 빠뜨렸어요.",
    extra: 2,
    note: "현장 미팅",
    nth: 5,
    status: "rejected",
    decisionNote: "그날 현장 미팅 일정이 없어요. 날짜를 다시 확인해 주세요.",
  },
  {
    userId: "u-pm",
    name: "노은진",
    reason: "다른 날 것을 잘못 적은 줄 알았어요.",
    extra: 0,
    note: "날짜 확인",
    nth: 2,
    status: "withdrawn",
  },
];

/** 다음 날 — 「yyyy-MM-dd」. */
const nextDay = (date: string) => {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const dailyEditRequests: DailyEditRequest[] = REQUESTS.flatMap((req, n) => {
  // 지난날의 Daily 를 다음 날 아침에 고쳐 달라고 한 것.
  const written = dailies
    .filter((d) => d.user_id === req.userId && d.rows.some((row) => row.actual_hours > 0))
    .sort((a, b) => b.work_date.localeCompare(a.work_date));
  const target = written[req.nth] ?? written[0];
  if (!target) return [];
  const asked = nextDay(target.work_date);
  const current = target.rows.map((row) => ({
    id: row.id,
    project_id: row.project_id,
    category_id: row.category_id,
    plan_description: row.plan_description,
    plan_hours: row.plan_hours,
    description: row.description,
    actual_hours: row.actual_hours,
    done: !!row.completed_at,
  }));
  // 첫 줄만 고친 모양. 승인된 요청은 이미 Daily 에 반영됐으니 지금 줄이 「이후」이고 「이전」을 되짚는다.
  const changed = current.map((row, i) =>
    i === 0
      ? {
          ...row,
          description: `${row.description} · ${req.note}`,
          actual_hours: row.actual_hours + req.extra,
        }
      : row
  );
  const approved = req.status === "approved";
  const reverted = current.map((row, i) =>
    i === 0 ? { ...row, actual_hours: Math.max(0, row.actual_hours - req.extra) } : row
  );
  const decided = req.status === "approved" || req.status === "rejected";
  return [
    {
      // 시드 표시를 붙인다 — handler 가 새 요청에 매기는 edit-request-N 과 겹치면 엉뚱한 요청을 찾는다.
      id: `edit-request-seed-${n + 1}`,
      daily_table_id: target.id,
      work_date: target.work_date,
      requester_id: req.userId,
      requester_name: req.name,
      reason: req.reason,
      before: approved ? reverted : current,
      after: approved ? current : changed,
      notes: null,
      status: req.status,
      decider_id: decided ? "u-owner" : null,
      decided_at: decided ? at(asked, "11:20") : null,
      decision_note: req.decisionNote ?? "",
      applied_at: approved ? at(asked, "11:20") : null,
      created_at: at(asked, `09:${String(10 + n * 7)}`),
    },
  ];
});
export const dailyImages: MockImage[] = [...fixedImages, ...taskImages];
